import { useEffect } from 'react';
import { BehaviorSubject } from 'rxjs';
import type { PublicAds, PublicShell } from '@ahora-patagonia/contracts';
import { useObservable } from '../hooks/useObservable.js';
import { useSsrPayload } from '../ssr/SsrDataProvider.js';

/**
 * Lo que **toda** respuesta pública trae por convención y el layout necesita: el shell
 * (marca, categorías con conteo, redes) y los avisos resueltos por ubicación.
 */
export interface PaginaPublica {
  shell: PublicShell;
  ads: PublicAds;
}

/**
 * Estado de la página actual para el layout, en navegación client-side.
 *
 * En el server este store **no se escribe nunca** (es un singleton de módulo y las renders
 * concurrentes lo pisarían entre requests): ahí el layout lee del payload SSR, que es por
 * request. En el cliente, cada vista lo actualiza al terminar su fetch con
 * `useSincronizarPagina`, y la cabecera/pie siguen a la página visible.
 */
const paginaStore = new BehaviorSubject<PaginaPublica | null>(null);

function dePayload(data: unknown): PaginaPublica | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { shell?: PublicShell; ads?: PublicAds };
  if (!d.shell) return null;
  return { shell: d.shell, ads: d.ads ?? {} };
}

/**
 * Shell + ads de la página actual. Orden de resolución: lo que puso la última vista en el
 * cliente → lo que vino del SSR → `null` (primer render de una navegación sin datos aún).
 */
export function usePaginaPublica(): PaginaPublica | null {
  const delStore = useObservable(paginaStore);
  const payload = useSsrPayload();
  return delStore ?? dePayload(payload?.data);
}

/** Las vistas lo llaman con su data cargada; en SSR no corre (es un efecto). */
export function useSincronizarPagina(pagina: PaginaPublica | null | undefined): void {
  useEffect(() => {
    if (pagina?.shell) paginaStore.next({ shell: pagina.shell, ads: pagina.ads ?? {} });
  }, [pagina]);
}
